import { useTranslation } from 'react-i18next';
import BaseButton from 'components/atoms/baseButton';
import { useState, type FC } from 'react';
import EmptyTablePlaceholder from 'components/atoms/base-empty-table-placeholder';
import BucketIcon from 'components/atoms/icons/bucketIcon';
import BaseLoader from 'components/atoms/base-loader';
import AddMedicationForm from 'components/molecules/admin-panel/forms/medication';
import BasePagination from 'components/atoms/base-pagination';
import BaseSelectBox from 'components/atoms/base-select-box';
import { isCombinedModule } from 'infrastructure/functions';
import { useCheckRoleAccess } from 'infrastructure/hooks';
import { Tabs, Divider } from 'antd';
import {
  DownOutlined,
  RightOutlined,
  UploadOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import FileUploadModal from 'components/molecules/csvUploadModal';
import { validateAndParseEhrCsv } from 'utils/validateAndParseEhrCsv';

import MedicationReconciliation from './components/medication-reconciliation';
import {
  useMedicationReconciliation,
  useImportMedications,
  diffItemToMedication,
} from './hooks/use-medication-reconciliation';
import s from './styles.module.scss';
import MedicationItem from './components/medication-item';
import PapCaseList from './components/pap-case-list';
import { SORT_OPTIONS, useMedication } from './hooks/use-medication';
import { usePapCases } from './hooks/use-pap-cases';

import type { PapCasesSortBy, PapCasesSortOrder } from './types';
import type { IMedication } from 'infrastructure/interfaces';
import type { ModuleType } from 'infrastructure/enums';

interface PatientMedicationProps {
  patientGuid: string;
  module: ModuleType;
  sessionId?: string | null;
}

const PatientMedication: FC<PatientMedicationProps> = ({
  patientGuid,
  module,
  sessionId,
}) => {
  const { t } = useTranslation();
  const [papPage, setPapPage] = useState(1);
  const [papSortBy, setPapSortBy] = useState<PapCasesSortBy>();
  const [papSortOrder, setPapSortOrder] = useState<PapCasesSortOrder>();
  const [isFileUploadModalOpen, setIsFileUploadModalOpen] = useState(false);
  const [importSkippedWarnings, setImportSkippedWarnings] = useState<string[]>(
    [],
  );
  const [reconciliationItemsFromImport, setReconciliationItemsFromImport] =
    useState<IMedication[]>([]);
  const {
    modalTitle,
    Alert,
    open,
    modifyItemGuid,
    medication,
    isLoading,
    addLoading,
    editLoading,
    loading,
    patientInfo,
    disableButton,
    wrapperRef,
    OPTIONS,
    status,
    page,
    itemPerPage,
    medicationForUpdate,
    showTrackingPlatform,
    documentationTimeSort,
    setDocumentationTimeSort,
    onCancelForm,
    handleDelete,
    handleEdit,
    scrollToTop,
    onSubmit,
    setStatus,
    setPage,
    setItemPerPage,
    toggleOpen,
    remove,
  } = useMedication({ patientGuid, module, sessionId });
  const {
    papCases,
    count: papCasesCount,
    isLoading: papCasesLoading,
    isFetching: papCasesFetching,
    downloadPapForm,
  } = usePapCases(patientGuid, {
    page: papPage,
    itemsPerPage: 5,
    sortBy: papSortBy,
    sortOrder: papSortOrder,
  });
  const { isReadOnlyRole } = useCheckRoleAccess();
  const [isExpanded, setIsExpanded] = useState(true);

  const disableActions = isCombinedModule(module)
    ? isReadOnlyRole
    : disableButton;

  const {
    items: reconciliationItems,
    lastSync: reconciliationLastSync,
    source: reconciliationSource,
    isLoading: reconciliationLoading,
    isRemediating,
    activeReconciliationId,
    setActiveReconciliationId,
    acceptItem,
    ignoreItem,
    acceptAll,
    ignoreAll,
  } = useMedicationReconciliation(patientGuid);

  const { importMedications } = useImportMedications(patientGuid);

  const hasActiveReconciliation = Boolean(activeReconciliationId);

  const [remediatingGuid, setRemediatingGuid] = useState<string | undefined>(
    undefined,
  );
  const [itemActionType, setItemActionType] = useState<
    'accept' | 'ignore' | null
  >(null);
  const [bulkActionType, setBulkActionType] = useState<
    'acceptAll' | 'ignoreAll' | null
  >(null);

  const handleAccept = async (item: { guid: string }) => {
    setBulkActionType(null);
    setRemediatingGuid(item.guid);
    setItemActionType('accept');
    try {
      await acceptItem(item.guid);
      setReconciliationItemsFromImport((prev) => {
        const next = prev.filter((i) => i.guid !== item.guid);
        if (next.length === 0) {
          setImportSkippedWarnings([]);
        }
        return next;
      });
    } finally {
      setRemediatingGuid(undefined);
      setItemActionType(null);
    }
  };

  const handleIgnore = async (guid: string) => {
    setBulkActionType(null);
    setRemediatingGuid(guid);
    setItemActionType('ignore');
    try {
      await ignoreItem(guid);
      setReconciliationItemsFromImport((prev) => {
        const next = prev.filter((i) => i.guid !== guid);
        if (next.length === 0) {
          setImportSkippedWarnings([]);
        }
        return next;
      });
    } finally {
      setRemediatingGuid(undefined);
      setItemActionType(null);
    }
  };

  const handleAcceptAll = async () => {
    setRemediatingGuid(undefined);
    setItemActionType(null);
    setBulkActionType('acceptAll');
    const importedItemIds =
      reconciliationItemsFromImport.length > 0
        ? reconciliationItemsFromImport
            .filter((i) => i.type !== 'unrecognized')
            .map((i) => i.guid)
        : undefined;
    try {
      await acceptAll(importedItemIds);
      setReconciliationItemsFromImport([]);
      setImportSkippedWarnings([]);
    } finally {
      setBulkActionType(null);
    }
  };

  const handleIgnoreAll = async () => {
    setRemediatingGuid(undefined);
    setItemActionType(null);
    setBulkActionType('ignoreAll');
    const importedItemIds =
      reconciliationItemsFromImport.length > 0
        ? reconciliationItemsFromImport.map((i) => i.guid)
        : undefined;
    try {
      await ignoreAll(importedItemIds);
      setReconciliationItemsFromImport([]);
      setImportSkippedWarnings([]);
    } finally {
      setBulkActionType(null);
    }
  };

  const uploadCsvFile = async (file: File) => {
    const text = await file.text();
    const result = validateAndParseEhrCsv(text);

    if (result.errors.length > 0) {
      // Translate error keys
      const translatedErrors = result.errors.map((error) =>
        error.startsWith('labels.') ? t(error as any) : error,
      );
      return { error: translatedErrors };
    }

    if (!result.data) {
      return { error: [t('labels.importMedicationsFailed')] };
    }

    try {
      const response = await importMedications(result.data);

      const firstDiffItemId = response?.diffItems?.[0]?.id;
      if (firstDiffItemId) {
        setActiveReconciliationId(firstDiffItemId);
      }

      if (response?.diffItems) {
        const mappedItems = response.diffItems
          .map((item) => diffItemToMedication(item))
          .filter((item): item is IMedication => item !== null);

        setReconciliationItemsFromImport(mappedItems);
      }

      if (result.skippedRowsErrors.length > 0) {
        setImportSkippedWarnings(result.skippedRowsErrors);
      }

      setIsFileUploadModalOpen(false);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : t('labels.importMedicationsFailed');
      return { error: [errorMessage] };
    }

    return {};
  };

  const medicationTabContent = (
    <>
      <div className={s['tab-header']}>
        <div className={s['header-select']}>
          <BaseSelectBox
            value={status}
            onChange={(v) => {
              remove();
              setPage(1);
              setStatus(v);
            }}
            options={OPTIONS}
            minWidth="110px"
          />
        </div>
        <div className={s['header-select']}>
          <BaseSelectBox
            value={documentationTimeSort || undefined}
            onChange={(v) => {
              setDocumentationTimeSort(v ?? '');
            }}
            options={SORT_OPTIONS}
            minWidth="110px"
          />
        </div>
        <BaseButton
          type="secondary"
          icon={<UploadOutlined />}
          label={t('labels.importFromEHR')}
          disabled={disableActions || loading || hasActiveReconciliation}
          onClick={() => setIsFileUploadModalOpen(true)}
        />
        <BaseButton
          type="primary"
          dataCy="addMedication-button"
          icon={<PlusOutlined />}
          label={`${t('controls.addMedication')}`}
          onClick={toggleOpen}
          loading={addLoading}
          disabled={disableActions || loading || hasActiveReconciliation}
        />
      </div>
      <BaseLoader loading={isLoading}>
        <div className={s.body} style={{ position: 'relative' }}>
          <BaseLoader loading={reconciliationLoading}>
            {' '}
            {!!(
              reconciliationItemsFromImport.length || reconciliationItems.length
            ) && (
              <>
                <MedicationReconciliation
                  items={
                    reconciliationItemsFromImport.length > 0
                      ? reconciliationItemsFromImport
                      : reconciliationItems
                  }
                  lastSync={reconciliationLastSync}
                  source={reconciliationSource}
                  disableActions={disableActions || reconciliationLoading}
                  isRemediating={isRemediating}
                  remediatingGuid={remediatingGuid}
                  remediatingActionType={itemActionType}
                  bulkActionType={bulkActionType}
                  modifyItemGuid={undefined}
                  onAccept={handleAccept}
                  onIgnore={handleIgnore}
                  onAcceptAll={handleAcceptAll}
                  onIgnoreAll={handleIgnoreAll}
                  importSkippedWarnings={importSkippedWarnings}
                  onCloseWarnings={() => setImportSkippedWarnings([])}
                />
                <div style={{ padding: '1.5rem 0' }}>
                  <Divider style={{ margin: '0' }} />
                </div>
              </>
            )}
          </BaseLoader>
          {!!medication?.items.length && (
            <div className={s['items-wrapper']}>
              {medication?.items.map((item) => {
                return (
                  <MedicationItem
                    key={item.guid}
                    item={item}
                    disableActions={disableActions}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    loading={editLoading}
                    modifyItemGuid={modifyItemGuid}
                  />
                );
              })}
            </div>
          )}
          {!medication?.items.length &&
            !reconciliationItems.length &&
            !reconciliationItemsFromImport.length && (
              <EmptyTablePlaceholder
                text={t('labels.medicationHistoryIsEmpty')}
                icon={<BucketIcon width={32} height={32} />}
              />
            )}
          <BasePagination
            page={page}
            total={medication?.count}
            align="end"
            pageSizeOptions={[5, 10, 15, 20, 25, 30, 100]}
            onChange={(newPage) => {
              scrollToTop();
              setPage(newPage);
            }}
            pageSize={itemPerPage}
            onShowSizeChange={(current, size) => {
              scrollToTop();
              setItemPerPage(size);
            }}
            hide={(medication?.count ?? 0) < 5}
          />
        </div>
      </BaseLoader>
      <FileUploadModal
        isOpen={isFileUploadModalOpen}
        toggle={() => setIsFileUploadModalOpen(!isFileUploadModalOpen)}
        title={t('labels.importFromEHRCapitalize')}
        sampleFileUrl="/csvTemplates/importFromEHRTemplate.csv"
        saveButtonLabel={t('controls.import')}
        width="650px"
        uploadCsvFile={uploadCsvFile}
      />
    </>
  );

  const tabItems = [
    {
      key: 'medication',
      label: t('labels.medication'),
      children: medicationTabContent,
    },
    {
      key: 'medication-history',
      label: t('labels.medicationHistory'),
      children: (
        <EmptyTablePlaceholder
          text={t('labels.medicationHistoryIsEmpty')}
          icon={<BucketIcon width={32} height={32} />}
        />
      ),
    },
    {
      key: 'prescription',
      label: t('labels.prescriptionAssistanceProgram_PAP'),
      children: (
        <PapCaseList
          items={papCases}
          loading={papCasesLoading || papCasesFetching}
          onDownloadPapForm={downloadPapForm}
          page={papPage}
          total={papCasesCount}
          onPageChange={setPapPage}
          onSortChange={(sortBy, sortOrder) => {
            setPapSortBy(sortBy);
            setPapSortOrder(sortOrder);
            setPapPage(1);
          }}
          patientGuid={patientGuid}
          patientInfo={patientInfo}
        />
      ),
    },
  ];

  return (
    <>
      <div className={s.wrapper} ref={wrapperRef} id="medication">
        <div className={s.header}>
          <BaseButton
            type="secondary"
            className={s['toggle-button']}
            icon={isExpanded ? <DownOutlined /> : <RightOutlined />}
            onClick={() => setIsExpanded(!isExpanded)}
          />
          <h4 className={s.title}>{t('labels.medications')}</h4>
        </div>
        {isExpanded && (
          <Tabs
            defaultActiveKey="medication"
            items={tabItems}
            className={s.tabs}
          />
        )}
      </div>

      <AddMedicationForm
        loading={addLoading || editLoading}
        onSubmit={onSubmit}
        onCancelForm={onCancelForm}
        defaultValues={medicationForUpdate}
        modalTitle={modalTitle}
        open={open}
        showTrackingPlatform={!medicationForUpdate && showTrackingPlatform}
      />
      {Alert}
    </>
  );
};
export default PatientMedication;
